import React, { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { CheckCircle2, XCircle, Trash2, ShieldCheck, Hourglass, Users as UsersIcon, Pencil, KeyRound, Globe, UserPlus } from "lucide-react";
import { useConfirm } from "../components/ConfirmDialog";
import { Modal } from "../components/Modal";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BLANK_CREATE = {
  email: "",
  name: "",
  password: "",
  role: "estimator",
  branch: "Plymouth",
  can_add_hours: true,
  can_subtract_hours: true,
  can_download_attachments: true,
  bypass_ip_restrictions: false,
  view_all_quotes: false,
  allowed_ips_text: "",
};

const ADMIN_TABS = [
  { key: "pending", label: "Pending",  icon: Hourglass },
  { key: "users",   label: "Users",    icon: UsersIcon }, // merged approved + rejected
];
// Managers only see the active-users tab — pending approvals + suspended
// reinstates stay admin-only because they require role/branch changes.
const MANAGER_TABS = [{ key: "users", label: "Users", icon: UsersIcon }];

export default function UsersPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const isManager = me?.role === "manager";
  const TABS = isAdmin ? ADMIN_TABS : MANAGER_TABS;
  const [tab, setTab] = useState(isAdmin ? "pending" : "users");
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetErr, setResetErr] = useState("");
  const [resetOk, setResetOk] = useState("");
  const [createDraft, setCreateDraft] = useState(null); // null = closed, object = open
  const [createBusy, setCreateBusy] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const confirm = useConfirm();

  const load = async () => {
    if (tab === "pending") {
      const r = await api.get("/users?status=pending");
      setUsers(r.data);
    } else {
      // Merged "Users" tab: approved + rejected, approved first.
      const [a, r] = await Promise.all([
        api.get("/users?status=approved"),
        api.get("/users?status=rejected"),
      ]);
      setUsers([...a.data, ...r.data]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const setStatus = async (u, status) => {
    await api.put(`/users/${u.id}`, { status });
    load();
  };
  const rejectPending = async (u) => {
    const ok = await confirm({
      title: `Reject registration for ${u.name || u.email}?`,
      description: "They will be moved to the suspended list and won't be able to log in. You can reinstate them later.",
      confirmText: "Reject registration",
      cancelText: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    await setStatus(u, "rejected");
  };
  const revoke = async (u) => {
    const ok = await confirm({
      title: `Revoke access for ${u.name || u.email}?`,
      description: "They will be signed out immediately and unable to log in until you reinstate them. The account stays in the list.",
      confirmText: "Revoke access",
      cancelText: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    await setStatus(u, "rejected");
  };
  const reinstate = async (u) => {
    const ok = await confirm({
      title: `Reinstate ${u.name || u.email}?`,
      description: "They will be able to log in again with their original password. Make sure this is the right person before approving.",
      confirmText: "Reinstate",
      cancelText: "Cancel",
    });
    if (!ok) return;
    await setStatus(u, "approved");
  };
  const setRole = async (u, role) => {
    await api.put(`/users/${u.id}`, { role });
    load();
  };
  const remove = async (u) => {
    const ok = await confirm({
      title: `DELETE user ${u.email}?`,
      description: "This permanently removes the account from the database. Use Revoke instead if you may want to reinstate later.",
      confirmText: "Delete permanently",
      cancelText: "Cancel",
      destructive: true,
      requireTypedText: "DELETE",
    });
    if (!ok) return;
    await api.delete(`/users/${u.id}`);
    load();
  };
  const saveEdit = async () => {
    const ips = (edit.allowed_ips_text || "")
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    await api.put(`/users/${edit.id}`, {
      branch: edit.branch,
      role: edit.role,
      allowed_ips: ips,
      can_add_hours: edit.can_add_hours !== false,
      can_subtract_hours: edit.can_subtract_hours !== false,
      can_download_attachments: edit.can_download_attachments !== false,
      bypass_ip_restrictions: edit.bypass_ip_restrictions === true,
      view_all_quotes: edit.view_all_quotes === true,
    });
    setEdit(null);
    load();
  };

  const submitCreate = async () => {
    setCreateBusy(true);
    setCreateErr("");
    try {
      const ips = (createDraft.allowed_ips_text || "")
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/users", {
        email: createDraft.email.trim().toLowerCase(),
        password: createDraft.password,
        name: createDraft.name.trim(),
        role: createDraft.role,
        branch: createDraft.branch,
        allowed_ips: ips,
        can_add_hours: createDraft.can_add_hours !== false,
        can_subtract_hours: createDraft.can_subtract_hours !== false,
        can_download_attachments: createDraft.can_download_attachments !== false,
        bypass_ip_restrictions: createDraft.bypass_ip_restrictions === true,
        view_all_quotes: createDraft.view_all_quotes === true,
      });
      toast.success(`Created ${createDraft.email} — they can sign in immediately.`);
      setCreateDraft(null);
      // Jump to the active-users tab so the new user is visible
      if (tab !== "users") setTab("users"); else load();
    } catch (e) {
      setCreateErr(e?.response?.data?.detail || "Failed to create user");
    } finally {
      setCreateBusy(false);
    }
  };

  const openReset = (u) => {
    setResetTarget(u); setResetPwd(""); setResetErr(""); setResetOk("");
  };

  const submitReset = async () => {
    setResetErr(""); setResetOk("");
    if ((resetPwd || "").length < 6) { setResetErr("Password must be at least 6 characters."); return; }
    setResetBusy(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPwd });
      setResetOk(`Password updated for ${resetTarget.email}. Copy it and share via a secure channel.`);
    } catch (ex) {
      setResetErr(ex.response?.data?.detail?.toString() || ex.message);
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{isAdmin ? "Admin" : "Depot Manager"}</div>
          <h1 className="qb-section-title text-3xl sm:text-4xl flex items-center gap-3"><UsersIcon size={28} className="text-brand-blue" /> User access</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? "Approve pending registrations, manage roles and branch access."
              : `Showing users in your depot (${me?.branch || "—"}). You can reset passwords for managers and estimators in your team, including yourself. Role and depot changes are admin-only.`
            }
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { setCreateErr(""); setCreateDraft({ ...BLANK_CREATE }); }}
            className="shrink-0"
            data-testid="create-user-btn"
          >
            <UserPlus size={14} className="mr-2" />
            New user
          </Button>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`-mb-px px-4 h-10 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${
              tab === t.key ? "border-brand-blue text-brand-blue" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <div className="qb-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="bg-slate-50/60 border-b border-slate-100">
              {(tab === "pending"
                ? ["Email", "Name", "Branch", "Role", "Registered", ""]
                : ["Email", "Name", "Branch", "Role", "Status", "Registered", ""]
              ).map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={tab === "pending" ? 6 : 7} className="p-10 text-center text-slate-400 text-sm">No {tab === "pending" ? "pending" : ""} users.</td></tr>
            )}
            {users.map((u) => {
              const isSuspended = u.status === "rejected";
              return (
                <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${isSuspended ? "opacity-70" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-mono">{u.email}</td>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700">{u.branch || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`qb-pill ${u.role === "admin" ? "bg-brand-orange text-white" : u.role === "manager" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                      {u.role === "admin" && <ShieldCheck size={11} />} {u.role === "manager" && <ShieldCheck size={11} />} {u.role}
                    </span>
                    {u.bypass_ip_restrictions && (
                      <span className="qb-pill bg-emerald-50 text-emerald-700 border border-emerald-100 ml-1.5 inline-flex items-center gap-1" title="This user bypasses the global and per-user IP allow-lists." data-testid={`bypass-ip-pill-${u.id}`}>
                        <Globe size={10} /> Anywhere
                      </span>
                    )}
                  </td>
                  {tab !== "pending" && (
                    <td className="px-4 py-3">
                      {isSuspended ? (
                        <span className="qb-pill bg-amber-50 text-amber-700 border border-amber-100" data-testid={`status-suspended-${u.id}`}>Suspended</span>
                      ) : (
                        <span className="qb-pill bg-emerald-50 text-emerald-700 border border-emerald-100" data-testid={`status-active-${u.id}`}>Active</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs text-slate-500">{u.created_at?.split("T")[0]}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    {tab === "pending" && (
                      <>
                        <button data-testid={`approve-${u.id}`} onClick={() => setStatus(u, "approved")} className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-brand-blue text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand-blue-dark">
                          <CheckCircle2 size={12} /> Approve
                        </button>
                        <button onClick={() => rejectPending(u)} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-red-200 text-red-600 text-xs font-semibold uppercase tracking-wider hover:bg-red-50" data-testid={`reject-${u.id}`}>
                          <XCircle size={12} /> Reject
                        </button>
                      </>
                    )}
                    {tab !== "pending" && (
                      <>
                        {isAdmin && (
                          <button onClick={() => setEdit({ ...u, allowed_ips_text: (u.allowed_ips || []).join("\n") })} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-slate-200 text-slate-700 text-xs font-semibold uppercase tracking-wider hover:border-brand-blue hover:text-brand-blue" data-testid={`edit-${u.id}`}>
                            <Pencil size={12} /> Edit
                          </button>
                        )}
                        <button onClick={() => openReset(u)} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-slate-200 text-slate-700 text-xs font-semibold uppercase tracking-wider hover:border-brand-blue hover:text-brand-blue" data-testid={`reset-${u.id}`}>
                          <KeyRound size={12} /> Reset password
                        </button>
                        {isAdmin && u.role !== "admin" && !isSuspended && (
                          <button onClick={() => setRole(u, "admin")} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-brand-orange text-brand-orange text-xs font-semibold uppercase tracking-wider hover:bg-brand-orange hover:text-white" data-testid={`make-admin-${u.id}`}>
                            <ShieldCheck size={12} /> Make admin
                          </button>
                        )}
                        {isAdmin && (isSuspended ? (
                          <button onClick={() => reinstate(u)} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-emerald-300 text-emerald-700 text-xs font-semibold uppercase tracking-wider hover:bg-emerald-50" data-testid={`reinstate-${u.id}`}>
                            <CheckCircle2 size={12} /> Reinstate
                          </button>
                        ) : (
                          <button onClick={() => revoke(u)} className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-red-200 text-red-600 text-xs font-semibold uppercase tracking-wider hover:bg-red-50" data-testid={`revoke-${u.id}`}>
                            <XCircle size={12} /> Revoke
                          </button>
                        ))}
                      </>
                    )}
                    {isAdmin && (
                      <button onClick={() => remove(u)} className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200" data-testid={`delete-${u.id}`} title="Delete permanently">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit ? `Edit ${edit.name}` : ""}
        subtitle={edit?.email}
        size="xl"
        data-testid="edit-user-dialog"
        footer={edit && (
          <>
            <button onClick={() => setEdit(null)} className="h-9 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold">Cancel</button>
            <button onClick={saveEdit} className="h-9 px-4 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark" data-testid="save-user-edit">Save</button>
          </>
        )}
      >
        {edit && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 text-sm">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Branch</label>
                <select value={edit.branch || ""} onChange={(e) => setEdit({ ...edit, branch: e.target.value })} className="qb-input" data-testid="edit-user-branch">
                  <option value="">— No branch (sees only own quotes) —</option>
                  <option value="Plymouth">Plymouth</option>
                  <option value="Bristol">Bristol</option>
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Users only see quotes belonging to their branch.
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Role</label>
                <select value={edit.role || "estimator"} onChange={(e) => setEdit({ ...edit, role: e.target.value })} className="qb-input" data-testid="edit-user-role">
                  <option value="estimator">Estimator</option>
                  <option value="manager">Manager (can override markup &amp; hours floor)</option>
                  <option value="admin">Admin (sees all quotes)</option>
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  <strong>Estimator</strong> &mdash; sees their branch's quotes; markup and hours locked to defaults unless individually permitted below.<br />
                  <strong>Manager</strong> &mdash; same visibility, but can go below the 35% markup floor and below auto-filled hours (always with initials/timestamp audit).<br />
                  <strong>Admin</strong> &mdash; sees and manages everyone's quotes.
                </div>
              </div>
              <div className="md:col-span-2 border-t border-slate-100 pt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">Sign-in network access</div>
                <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={edit.bypass_ip_restrictions === true}
                    onChange={(e) => setEdit({ ...edit, bypass_ip_restrictions: e.target.checked })}
                    data-testid="edit-bypass-ip-restrictions"
                    className="mt-1"
                  />
                  <span>
                    <span className="font-semibold inline-flex items-center gap-1.5"><Globe size={12} className="text-emerald-600" /> Connect from anywhere</span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">Bypass <strong>both</strong> the global IP allow-list and this user's personal IP list. Use for admins and remote workers with dynamic IPs.</span>
                  </span>
                </label>
                <div className={`mt-3 ${edit.bypass_ip_restrictions === true ? "opacity-50 pointer-events-none" : ""}`}>
                  <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Allowed IP addresses (this user only)</label>
                  <textarea
                    rows={3}
                    value={edit.allowed_ips_text || ""}
                    onChange={(e) => setEdit({ ...edit, allowed_ips_text: e.target.value })}
                    className="qb-input font-mono text-xs"
                    placeholder="One IP per line (e.g. 203.0.113.42)"
                    data-testid="edit-user-allowed-ips"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Additional IPs this user can sign in from, on top of the global allow-list. Leave blank to use only the global list. Ignored when "Connect from anywhere" is on.
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">Auto-hours lock (Quote Builder)</div>
                <p className="text-[11px] text-slate-500 mb-2">When a product + work type is chosen on a labour row, the hours auto-fill from the catalogue. These two switches control what this user can do to that auto-filled value.</p>
                <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={edit.can_add_hours !== false}
                    onChange={(e) => setEdit({ ...edit, can_add_hours: e.target.checked })}
                    data-testid="edit-can-add-hours"
                    className="mt-1"
                  />
                  <span><span className="font-semibold">Can ADD hours</span> &mdash; allow this user to increase the auto-filled hours figure.</span>
                </label>
                <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={edit.can_subtract_hours !== false}
                    onChange={(e) => setEdit({ ...edit, can_subtract_hours: e.target.checked })}
                    data-testid="edit-can-subtract-hours"
                    className="mt-1"
                  />
                  <span><span className="font-semibold">Can SUBTRACT hours</span> &mdash; allow this user to decrease the auto-filled hours figure.</span>
                </label>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">Attachments</div>
                <p className="text-[11px] text-slate-500 mb-2">Estimators can always see the attachments badge and filenames on the Quote Detail page. This switch controls whether they can open or download the actual files.</p>
                <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={edit.can_download_attachments !== false}
                    onChange={(e) => setEdit({ ...edit, can_download_attachments: e.target.checked })}
                    data-testid="edit-can-download-attachments"
                    className="mt-1"
                  />
                  <span><span className="font-semibold">Can download attachments</span> &mdash; allow this user to download supplier PDFs, images and `.msg` files attached to quotes.</span>
                </label>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">Quote visibility</div>
                <p className="text-[11px] text-slate-500 mb-2">By default users see only quotes from their own depot plus any they created themselves. Toggle this on to grant cross-depot read access without granting admin powers.</p>
                <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={edit.view_all_quotes === true}
                    onChange={(e) => setEdit({ ...edit, view_all_quotes: e.target.checked })}
                    data-testid="edit-view-all-quotes"
                    className="mt-1"
                  />
                  <span><span className="font-semibold">View all quotes (all depots)</span> &mdash; useful for senior estimators or owners who need full visibility. Edit rights stay scoped to their role.</span>
                </label>
              </div>
          </div>
        )}
      </Modal>

      {/* Create user modal — admin-only direct provisioning (skips the
          self-register + approve flow). Same field set as the Edit modal. */}
      <Modal
        open={!!createDraft}
        onClose={() => createBusy ? null : setCreateDraft(null)}
        title="Create user"
        icon={UserPlus}
        size="md"
        data-testid="create-user-modal"
        footer={createDraft && (
          <>
            <button onClick={() => setCreateDraft(null)} disabled={createBusy} className="h-9 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold disabled:opacity-60">Cancel</button>
            <button onClick={submitCreate} disabled={createBusy} className="h-9 px-4 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark disabled:opacity-60" data-testid="create-user-submit">
              {createBusy ? "Creating…" : "Create user"}
            </button>
          </>
        )}
      >
        {createDraft && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <p className="text-[12px] text-slate-500 leading-relaxed">
                The new user is created with status <strong>approved</strong> and can sign in immediately with the password you set below. They will not need to self-register or be approved separately.
              </p>
            </div>
            {createErr && (
              <div className="md:col-span-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="create-user-err">{createErr}</div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Email</label>
              <input type="email" value={createDraft.email} onChange={(e) => setCreateDraft({ ...createDraft, email: e.target.value })} className="qb-input" placeholder="estimator@entrygroup.co.uk" data-testid="create-user-email" autoFocus />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Display name</label>
              <input type="text" value={createDraft.name} onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })} className="qb-input" placeholder="Jane Smith" data-testid="create-user-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Initial password</label>
              <input type="text" value={createDraft.password} onChange={(e) => setCreateDraft({ ...createDraft, password: e.target.value })} className="qb-input font-mono" placeholder="Min. 6 characters" data-testid="create-user-password" />
              <div className="text-[11px] text-slate-500 mt-1">
                Share this with the user securely. They can change it via the login page&apos;s &quot;Forgot password?&quot; link.
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Branch</label>
              <select value={createDraft.branch} onChange={(e) => setCreateDraft({ ...createDraft, branch: e.target.value })} className="qb-input" data-testid="create-user-branch">
                <option value="Plymouth">Plymouth</option>
                <option value="Bristol">Bristol</option>
                <option value="">(no branch — admin only)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Role</label>
              <select value={createDraft.role} onChange={(e) => setCreateDraft({ ...createDraft, role: e.target.value })} className="qb-input" data-testid="create-user-role">
                <option value="estimator">Estimator</option>
                <option value="manager">Manager (can override markup &amp; hours floor)</option>
                <option value="admin">Admin (sees all quotes)</option>
              </select>
            </div>
            <div className="md:col-span-2 border-t border-slate-100 pt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">Sign-in network access</div>
              <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={createDraft.bypass_ip_restrictions === true} onChange={(e) => setCreateDraft({ ...createDraft, bypass_ip_restrictions: e.target.checked })} data-testid="create-bypass-ip-restrictions" className="mt-1" />
                <span>
                  <span className="font-semibold inline-flex items-center gap-1.5"><Globe size={12} className="text-emerald-600" /> Connect from anywhere</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">Bypass both the global IP allow-list and this user&apos;s personal IP list.</span>
                </span>
              </label>
              <div className={`mt-3 ${createDraft.bypass_ip_restrictions === true ? "opacity-50 pointer-events-none" : ""}`}>
                <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Allowed IP addresses (this user only)</label>
                <textarea rows={2} value={createDraft.allowed_ips_text} onChange={(e) => setCreateDraft({ ...createDraft, allowed_ips_text: e.target.value })} className="qb-input font-mono text-xs" placeholder="One IP per line (e.g. 203.0.113.42)" data-testid="create-user-allowed-ips" />
              </div>
            </div>
            <div className="md:col-span-2 border-t border-slate-100 pt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">Quote Builder permissions</div>
              <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={createDraft.can_add_hours !== false} onChange={(e) => setCreateDraft({ ...createDraft, can_add_hours: e.target.checked })} data-testid="create-can-add-hours" className="mt-1" />
                <span><span className="font-semibold">Can ADD hours</span> &mdash; increase the auto-filled hours on labour rows.</span>
              </label>
              <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={createDraft.can_subtract_hours !== false} onChange={(e) => setCreateDraft({ ...createDraft, can_subtract_hours: e.target.checked })} data-testid="create-can-subtract-hours" className="mt-1" />
                <span><span className="font-semibold">Can SUBTRACT hours</span> &mdash; decrease the auto-filled hours on labour rows.</span>
              </label>
              <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={createDraft.can_download_attachments !== false} onChange={(e) => setCreateDraft({ ...createDraft, can_download_attachments: e.target.checked })} data-testid="create-can-download-attachments" className="mt-1" />
                <span><span className="font-semibold">Can download attachments</span> &mdash; open supplier PDFs and files attached to quotes.</span>
              </label>
              <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={createDraft.view_all_quotes === true} onChange={(e) => setCreateDraft({ ...createDraft, view_all_quotes: e.target.checked })} data-testid="create-view-all-quotes" className="mt-1" />
                <span><span className="font-semibold">View all quotes (all depots)</span> &mdash; grant cross-depot read access. Edit rights still scoped to their role.</span>
              </label>
            </div>
          </div>
        )}
      </Modal>


      <Modal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        title={resetTarget ? `Reset password — ${resetTarget.email}` : ""}
        icon={KeyRound}
        size="sm"
        data-testid="reset-password-modal"
        footer={resetTarget && (
          <>
            <button onClick={() => setResetTarget(null)} className="h-9 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold">{resetOk ? "Close" : "Cancel"}</button>
            {!resetOk && (
              <button disabled={resetBusy} onClick={submitReset} className="h-9 px-4 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark disabled:opacity-60" data-testid="reset-password-submit">
                {resetBusy ? "Saving…" : "Save new password"}
              </button>
            )}
          </>
        )}
      >
        {resetTarget && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600 text-xs">Set a new password directly. You'll need to share it with the user via a secure channel — there is no email sent for this flow.</p>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">New password</label>
              <input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="qb-input font-mono" placeholder="At least 6 characters" data-testid="reset-password-input" />
            </div>
            {resetErr && <div className="border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2 rounded-lg" data-testid="reset-password-error">{resetErr}</div>}
            {resetOk && <div className="border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs px-3 py-2 rounded-lg" data-testid="reset-password-ok">{resetOk}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
